import type { HTMLAttributes } from 'react';
import clsx from 'clsx';

export type PillTone = 'warning' | 'success' | 'danger' | 'info' | 'neutral' | 'brand';
export type PillSize = 'sm' | 'lg';

export type PillProps = {
  tone?: PillTone;
  size?: PillSize;
  /** Render a small status dot before the label. */
  dot?: boolean;
} & HTMLAttributes<HTMLSpanElement>;

const base = clsx(
  'inline-flex items-center gap-1',
  'rounded-pill',
  'font-semibold leading-none whitespace-nowrap',
);

const sizes: Record<PillSize, string> = {
  sm: 'px-[10px] py-1 text-xs',
  lg: 'h-[27px] min-w-[98px] justify-center px-[18px] py-0 text-sm',
};

const tones: Record<PillTone, string> = {
  warning: 'bg-warning-bg text-warning-fg',
  success: 'bg-success-bg text-success-fg',
  danger: 'bg-danger-bg text-danger-fg',
  info: 'bg-info-bg text-info-fg',
  neutral: 'bg-ink-100 text-ink-700',
  brand: 'bg-purple-50 text-purple-700',
};

export function Pill({
  tone = 'neutral',
  size = 'sm',
  dot,
  className,
  children,
  ...rest
}: PillProps) {
  return (
    <span data-tone={tone} className={clsx(base, sizes[size], tones[tone], className)} {...rest}>
      {dot && (
        <span
          aria-hidden="true"
          data-pill-dot
          className="inline-block h-[6px] w-[6px] rounded-full bg-current"
        />
      )}
      {children}
    </span>
  );
}
